import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LocalMeetStorageInterface } from '../models/local-meet-storage.interface';
import { LocalUserStorageInterface } from '../models/local-user-storage.interface';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  constructor(@Inject(DOCUMENT) private document: Document) {}
  inMemoryStorage: { [key: string]: string } = {};
  localStorageSupported: boolean | null = null;

  public setLocalMeetStorage(athleticLiveMeetId: number, localMeetStorage: LocalMeetStorageInterface | null) {
    try {
      if (this.document.defaultView) {
        if (localMeetStorage) {
          return this.document.defaultView.localStorage.setItem(
            'localMeetStorage-' + athleticLiveMeetId,
            JSON.stringify(localMeetStorage),
          );
        } else {
          return this.document.defaultView.localStorage.removeItem('localMeetStorage-' + athleticLiveMeetId);
        }
      }
    } catch (e) {
      return null;
    }
  }

  public getLocalMeetStorage(athleticLiveMeetId: number): LocalMeetStorageInterface {
    const meetStorage = this.getLocalStorageItem('localMeetStorage-' + athleticLiveMeetId);
    if (
      meetStorage === null ||
      (meetStorage && DateTime.fromISO(meetStorage.expires).toMillis() < DateTime.now().toMillis())
    ) {
      return this.getDefaultLocalMeetStorage();
    } else {
      return meetStorage;
    }
  }

  public getLocalUserStorage(): LocalUserStorageInterface | null {
    return this.getLocalStorageItem('localUserStorage');
  }

  public setDisplayModeModePreference(isDarkMode: boolean) {
    if (this.isLocalStorageSupported()) {
      try {
        if (this.document.defaultView) {
          const content = this.document.defaultView.localStorage.getItem('localUserStorage');
          let data: LocalUserStorageInterface = this.getDefaultLocalUserStorage();
          if (content) {
            data = JSON.parse(content);
          }
          data.displayMode = isDarkMode ? 'dark' : 'light';
          return this.document.defaultView.localStorage.setItem('localUserStorage', JSON.stringify(data));
        }
        return null;
      } catch (e) {
        return null;
      }
    } else {
      const content = this.getInMemoryStorageItem('localUserStorage');
      let data: LocalUserStorageInterface = this.getDefaultLocalUserStorage();
      if (content) {
        data = JSON.parse(content);
      }
      data.displayMode = isDarkMode ? 'dark' : 'light';
      this.inMemoryStorage['localUserStorage'] = JSON.stringify(data);
      return;
    }
  }

  protected getInMemoryStorageItem(key: string): string | null {
    if (key in this.inMemoryStorage) {
      return this.inMemoryStorage[key];
    } else {
      return null;
    }
  }

  protected getLocalStorageItem(key: string) {
    if (this.isLocalStorageSupported()) {
      try {
        if (this.document.defaultView) {
          const content = this.document.defaultView.localStorage.getItem(key);
          if (content) {
            return JSON.parse(content);
          }
        }
        return null;
      } catch (e) {
        return null;
      }
    } else {
      const content = this.getInMemoryStorageItem(key);
      if (content) {
        return JSON.parse(content);
      } else {
        return null;
      }
    }
  }

  protected isLocalStorageSupported(): boolean {
    if (this.localStorageSupported === null) {
      try {
        if (this.document.defaultView) {
          const key = '__some_random_key_you_are_not_going_to_use__';
          this.document.defaultView.localStorage.setItem('testkey', 'testvalue');
          this.document.defaultView.localStorage.removeItem('testkey');
        }
        this.localStorageSupported = true;
        return true;
      } catch (e) {
        this.localStorageSupported = false;
        return false;
      }
    }
    return this.localStorageSupported;
  }

  private getDefaultLocalMeetStorage(): LocalMeetStorageInterface {
    const now = DateTime.now();
    return {
      expires: now.endOf('day').plus({ hours: 4 }).toISO() ?? '',
      eventFilter: {
        firstLevelFilter: null,
        secondLevelFilter: null,
        firstLevelFilterName: null,
      },
    };
  }

  private getDefaultLocalUserStorage(): LocalUserStorageInterface {
    return {
      displayMode: '',
    };
  }
}
